import { AppBuilderStreamAPIName } from 'src/constants';
import {
  type AppBuilderErrorIdentity,
  resolveAppBuilderErrorSource,
} from 'src/routes/appSetup/builder/utils/appBuilderErrors';
import { buildApiEndpointUrl, getInitForApi } from 'src/utils/AmplifyApiUtils';

import type {
  AppBuilderAttachment,
  ProductAgentQuestion,
  ProductAgentResponse,
} from './appBuilderApi';
import {
  AppBuilderRequestError,
  parseAppBuilderErrorPayload,
} from './appBuilderRequestError';
import { fetchBuilderHistoryPage } from './streamServiceFetch';

import { ApiMethods, ApiTags, appAPI, BUILDER_VIEW_CACHE_SECONDS } from '.';

// Roles are LLM-canonical: user / assistant / system. The kind of
// content a message carries is encoded by its parts (see MessagePart).
// An assistant message can interleave text and tool_use parts the way
// Anthropic's Messages API does; a system message carries deploy events
// or status text; a user message is the original prompt or future
// tool_result data.
export type BuilderMessageRole = 'user' | 'assistant' | 'system' | 'narrator';

// Part type discriminator. Renderers branch on this. New content kinds
// ship as new part types — `role` does not grow.
export type MessagePartType =
  | 'text'
  | 'tool_use'
  | 'tool_result'
  | 'task_list'
  | 'error'
  | 'thinking_v1'
  | 'log'
  | 'narrator_thinking'
  | 'ack'
  | 'product_agent_questions'
  | 'product_agent_prompt'
  | 'connection_request'
  | 'session_reset'
  | 'conversation'
  | 'performing_update'
  | 'update_progress'
  | 'test_preview'
  | 'suggested_prompts';

// type=suggested_prompts item — one follow-up the build agent proposed at the
// end of a code-changing turn. Mirrors backend builder_message.SuggestedPrompt.
export interface SuggestedPrompt {
  /** Chip label, two to four words. */
  title: string;
  /** What a click prefills in the composer, ready to send as-is. */
  prompt: string;
  /** Provider slug when the suggestion wires in an integration; the chip shows
   *  its brand mark. Absent otherwise. */
  connector?: string;
}

// Sent with a message the user started from a suggestion chip and stored on
// the user message. Mirrors backend builder_message.SuggestionOrigin.
export interface SuggestionOrigin {
  /** The assistant message carrying the chip's suggested_prompts part. */
  messageId: string;
  /** That message's turn. */
  turnId?: string;
  /** The chip's position in its set, 0-based. */
  index: number;
  /** True when the sent text differs from the chip's prompt. */
  edited: boolean;
  /** Where the suggestion was picked: absent for a chip in the chat, `email`
   *  for a link in the idle email. */
  source?: 'email';
}

// type=connection_request payload — an agent-emitted offer to connect one or
// more third-party providers the app needs but the workspace hasn't connected.
export interface ConnectionRequestPayload {
  providers: string[];
  reason?: string;
}

export interface ConnectionAskResolution {
  outcome: 'connected' | 'declined';
}

// MessagePart mirrors backend builder_message.MessagePart. Only the
// fields documented for that part type are populated; the rest are
// undefined.
export interface MessagePart {
  type: string;
  // present on text, tool_use, and thinking parts produced by build tasks
  subTaskId?: string;
  // type=text
  text?: string;
  // type=tool_use
  toolUseId?: string;
  toolName?: string;
  // Backend sends ToolInput as `json.RawMessage`, which serializes as
  // inline JSON (object/array/primitive) rather than a JSON-encoded
  // string. Typed as `unknown` so renderers must narrow before use.
  toolInput?: unknown;
  // type=tool_result
  toolResultFor?: string;
  toolResult?: string;
  toolIsError?: boolean;
  // type=task_list — `tasks` carries the inline task array (backend
  // serializes it from `json.RawMessage`, not as a JSON-encoded string);
  // `currentIndex` points at the task currently in flight.
  tasks?: unknown[];
  currentIndex?: number;
  // type=error
  errorMessage?: string;
  // On setup-failure error parts: the product_agent_prompt message id to
  // re-dispatch on Try again (approval path has no user row).
  retryMessageId?: string;
  // Structured tag for why a turn ended without completing (e.g. "user_stopped").
  // Set on the terminal error part so a deliberate stop is distinguishable from
  // an organic failure without parsing free text.
  reasonCode?: string;
  // type=thinking_v1
  thinkingText?: string;
  // type=log — raw model output, not for display
  logText?: string;
  // type=narrator_thinking — terse chain-of-thought summary
  narratorText?: string;
  // Streaming state; reused (beyond narrator_thinking) on text, conversation,
  // ack, and product_agent_prompt parts to gate the paced-reveal streaming cue.
  narratorStatus?: 'forming' | 'complete';
  narratorParagraphIndex?: number;
  // type=product_agent_questions
  questions?: ProductAgentQuestion[];
  // type=product_agent_prompt
  finalizedPrompt?: string;
  approvedAt?: string;
  // type=connection_request
  connectionRequest?: ConnectionRequestPayload;
  resolvedAt?: string;
  resolvedOutcome?: string;
  // Stamped on a user-turn part that resumed/declined a connection_request;
  // points at that ask part's id so the client can suppress its own bubble.
  connectionAskPartId?: string;
  // type=conversation — a direct conversational answer (role=assistant) that
  // precedes a coding turn for the same user message in a "split" response.
  conversationText?: string;
  // Shared by type=performing_update and type=test_preview — the
  // updates.Update.ID (registry entry) this part concerns.
  updateType?: string;
  // type=performing_update — registry Title/Description for the transcript
  // header. type=test_preview reuses the same two fields for its own
  // title/intro (the registry's AppliedTitle/AppliedIntro).
  updateTitle?: string;
  updateDescription?: string;
  // type=performing_update — the registry's Copy, stamped at kick-off so the
  // header renders tense-correct lede and row-title copy without the
  // frontend rewriting tenses itself as the turn runs and lands.
  runningLede?: string;
  appliedLede?: string;
  runningRowTitle?: string;
  appliedRowTitle?: string;
  // type=update_progress — the platform-written step lines of an update turn,
  // in the order each was first reached, and the key of the step running now.
  progressLines?: UpdateProgressLine[];
  activeKey?: string;
  // type=test_preview — registry TestInstructions is the fallback one-liner
  // shown when there are no structured testSteps; agentSummary is the update
  // turn's own account of what it changed, kept for the collapsed transcript
  // but never rendered on the card. publishedTitle replaces updateTitle once
  // the run publishes.
  testInstructions?: string;
  agentSummary?: string;
  publishedTitle?: string;
  // type=test_preview — structured "what to check in Preview" steps naming real
  // screens of the app. Empty falls back to testInstructions alone.
  testSteps?: UpdateTestStep[];
  // type=test_preview — overall verification state sourced from the platform QA
  // run linked to this update. Absent when the flag is off, no QA run is linked,
  // or the card predates verification.
  verificationState?: 'verifying' | 'all_passed' | 'one_failed';
  // type=test_preview — per-step verdicts from the QA run, in display order.
  // Each entry corresponds to one owner-facing step. Present with verificationState.
  stepVerdicts?: UpdateStepVerdict[];
  // type=test_preview — one quiet confirmation line shown below the step list for
  // the all_passed state (e.g. "We also confirmed the sign-in change works.").
  diffSummary?: string;
  // type=suggested_prompts — the turn's follow-up chips, in display order.
  suggestedPrompts?: SuggestedPrompt[];
}

// type=update_progress — one owner-facing step line.
export interface UpdateProgressLine {
  key: string;
  text: string;
}

// type=test_preview — one numbered step in the applied card's "What to check
// in Preview" list.
export interface UpdateTestStep {
  title: string;
  detail: string;
}

// type=test_preview — one step's verdict from the platform QA run. Used when
// verificationState is set on the part.
export interface UpdateStepVerdict {
  title: string;
  verdict: 'pass' | 'fail' | 'pending' | 'blocked';
  // Note shown on the failing row in the owner's language of what the QA agent saw.
  note?: string;
  // S3 key for the step's screen recording. Watch link renders when present.
  videoKey?: string;
}

export interface BuilderMessage {
  id: string;
  builderSessionId: string;
  role: string;
  parts: MessagePart[];
  turnId?: string;
  createdAt?: string;
  updatedAt?: string;
  attachments?: AppBuilderAttachment[];
  // First user message of a fresh context after a "New session" reset. The
  // transcript draws the new-session divider directly above this message.
  startsNewSession?: boolean;
  // Internal user id of the teammate who sent a user message. Empty on
  // messages written before sender tracking, and on system kick-offs.
  sentBy?: string;
}

/** Extract the concatenated text from a message's text parts. */
export const getMessageText = (msg: BuilderMessage): string =>
  (msg.parts ?? [])
    .filter((p) => p.type === 'text')
    .map((p) => p.text ?? '')
    .join('\n\n');

export interface CustomSkill {
  name: string;
  content: string;
}

export interface SkillsConfig {
  disabledBuiltins?: string[];
  customSkills?: CustomSkill[];
}

export interface BuilderSession {
  id: string;
  appBuilderId: string;
  claudeSessionId?: string;
  summary?: string;
  // Non-empty while a turn is in flight; cleared when the turn completes.
  // Drives the chat thinking indicator and composer lock.
  activeTurnId?: string;
  // "booting" | "running" while a turn lock is held; empty when idle.
  turnPhase?: 'booting' | 'running' | '';
  activeTurnMode?: 'plan' | 'update' | 'repo_move';
  activeTaskListMessageId?: string;
  // Ephemeral status phrase set by the narrator Haiku; cleared when the turn ends.
  narratorStatus?: string;
  // Set when a "New session" reset has cleared the agent's conversational
  // context. The app/build state is untouched — only this session's memory.
  contextResetAt?: string;
  // 0-100, normalized so the foundation + app context we push in before any
  // conversation reads as 0%. Undefined until the first turn of the current
  // ClaudeSessionID reports usage; cleared by the same reset that clears
  // contextResetAt. See backend ResetSession/handleDone.
  contextUsedPercent?: number;
  createdAt?: string;
  updatedAt?: string;
}

export const getBuilderSessionEntityTag = (id: string) => ({
  type: ApiTags.builderSessions as const,
  id,
});

export interface ListBuilderSessionsResponse {
  data: BuilderSession[];
  nextToken?: string;
}

export interface BuilderHistoryPage {
  messages: BuilderMessage[];
  nextToken: string;
}

// sinceCursor is the cursor the SSE `history-page` event handed out for the
// newest page. It is part of the key on purpose: older pages are only valid
// relative to the newest page they sit behind, so a session that gained
// messages while the user was away gets a fresh entry rather than a gap.
export interface BuilderHistoryArg {
  appBuilderId: string;
  sessionId: string;
  sinceCursor: string;
  portalId?: string;
}

// Hard cap on pages held per entry (pages are ~50 server-stripped rows each).
export const HISTORY_MAX_PAGES = 40;

export const builderSessionApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    // Older chat history, behind the newest page the SSE replays. Cached across
    // route unmounts so returning to an app does not re-page. Deliberately has
    // no tags: nothing should invalidate it (a session reset appends a marker
    // row over SSE rather than deleting history), and an entry simply expires.
    getBuilderSessionHistory: build.infiniteQuery<
      BuilderHistoryPage,
      BuilderHistoryArg,
      string
    >({
      infiniteQueryOptions: {
        // Overridden per hook call with the arg's sinceCursor. A refetch (the
        // retry after a failed first page) re-dispatches without that hook
        // option, so the queryFn falls back to the arg's cursor as well.
        initialPageParam: '',
        getNextPageParam: (lastPage, allPages, lastPageParam) =>
          !lastPage.nextToken ||
          lastPage.nextToken === lastPageParam ||
          allPages.length >= HISTORY_MAX_PAGES
            ? undefined
            : lastPage.nextToken,
        // A refetch must not re-request every cached page in sequence.
        refetchCachedPages: false,
      },
      queryFn: ({ queryArg, pageParam }, { signal }) =>
        fetchBuilderHistoryPage(
          queryArg,
          pageParam || queryArg.sinceCursor,
          signal,
        ),
      keepUnusedDataFor: BUILDER_VIEW_CACHE_SECONDS,
    }),
    listBuilderSessions: build.query<
      ListBuilderSessionsResponse,
      { appBuilderId: string }
    >({
      query: ({ appBuilderId }) => ({
        path: `/app-builders/${appBuilderId}/sessions`,
        method: ApiMethods.get,
        options: {},
      }),
      keepUnusedDataFor: BUILDER_VIEW_CACHE_SECONDS,
      providesTags: (_, __, arg) => [
        { type: ApiTags.builderSessions, id: `LIST-${arg.appBuilderId}` },
      ],
    }),
    getBuilderSession: build.query<
      BuilderSession,
      { appBuilderId: string; sessionId: string }
    >({
      query: ({ appBuilderId, sessionId }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${sessionId}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getBuilderSessionEntityTag(arg.sessionId)],
    }),
    sendBuilderSessionMessage: build.mutation<
      { data: { turnId: string; messageId: string } },
      {
        appBuilderId: string;
        builderSessionId: string;
        content?: string;
        // Set to "plan" to dispatch a planning turn — the assistant
        // message gets a task_list part appended on done.
        mode?: 'plan';
        // Optional prompt slot selections (slug → context item ID) from the
        // build inspector. Only honored when the app_builder_inspector flag
        // is on for the caller's portal.
        promptSelections?: Record<string, string>;
        // When true, dispatch the plan turn from the persisted
        // product_agent_prompt instead of sending fresh user content.
        // Requires mode === 'plan'. Backend resolves the prompt text
        // server-side and stamps approvedAt on the assistant part.
        approve?: boolean;
        // File attachments uploaded to the user-files bucket; echoed back
        // so the backend can presign GETs for the sandbox turn program.
        attachments?: AppBuilderAttachment[];
        // Per-turn model config. When set, the backend applies it to this turn
        // and persists it as the sticky default for later turns. Empty keeps
        // the last-used selection. effort is one of max|xhigh|high|medium|low.
        model?: string;
        effort?: string;
        // Clears the per-app model pin and puts the app back on the default,
        // recorded on the row so a later default change still reaches it.
        // Rejected by the backend when sent with model or effort.
        autoMode?: boolean;
        // Client-minted message id (UUID). The backend adopts it as the persisted
        // user message's id so the webapp reconciles its optimistic bubble by id.
        clientMessageId?: string;
        // The suggestion chip this message started from, when it did; the
        // backend stores it on the user message.
        suggestionOrigin?: SuggestionOrigin;
      }
    >({
      query: ({
        appBuilderId,
        builderSessionId,
        content,
        mode,
        promptSelections,
        approve,
        attachments,
        model,
        effort,
        autoMode,
        clientMessageId,
        suggestionOrigin,
      }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${builderSessionId}/messages`,
        method: ApiMethods.post,
        options: {
          body: approve
            ? {
                approve: true,
                mode,
                ...(model ? { model } : {}),
                ...(effort ? { effort } : {}),
                ...(autoMode ? { autoMode } : {}),
              }
            : {
                content,
                mode,
                ...(promptSelections && Object.keys(promptSelections).length > 0
                  ? { promptSelections }
                  : {}),
                ...(attachments && attachments.length > 0
                  ? { attachments }
                  : {}),
                ...(model ? { model } : {}),
                ...(effort ? { effort } : {}),
                ...(autoMode ? { autoMode } : {}),
                ...(clientMessageId ? { clientMessageId } : {}),
                ...(suggestionOrigin ? { suggestionOrigin } : {}),
              },
        },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: ApiTags.builderSessions, id: `LIST-${arg.appBuilderId}` },
      ],
    }),
    updateBuilderSessionSkillsConfig: build.mutation<
      BuilderSession,
      {
        appBuilderId: string;
        builderSessionId: string;
        skillsConfig: SkillsConfig;
      }
    >({
      query: ({ appBuilderId, builderSessionId, skillsConfig }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${builderSessionId}/skills-config`,
        method: ApiMethods.patch,
        options: { body: { skillsConfig } },
      }),
      invalidatesTags: (_, __, arg) => [
        getBuilderSessionEntityTag(arg.builderSessionId),
        { type: ApiTags.builderSessions, id: `LIST-${arg.appBuilderId}` },
      ],
    }),
    resetBuilderSession: build.mutation<
      BuilderSession,
      { appBuilderId: string; builderSessionId: string }
    >({
      query: ({ appBuilderId, builderSessionId }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${builderSessionId}/reset`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [
        getBuilderSessionEntityTag(arg.builderSessionId),
        { type: ApiTags.builderSessions, id: `LIST-${arg.appBuilderId}` },
      ],
    }),
    sendProductAgentSessionMessage: build.mutation<
      ProductAgentResponse,
      {
        appBuilderId: string;
        builderSessionId: string;
        content?: string;
        promptSelections?: Record<string, string>;
      }
    >({
      query: ({
        appBuilderId,
        builderSessionId,
        content,
        promptSelections,
      }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${builderSessionId}/product-agent`,
        method: ApiMethods.post,
        options: {
          body: {
            content: content ?? '',
            ...(promptSelections && Object.keys(promptSelections).length > 0
              ? { promptSelections }
              : {}),
          },
        },
      }),
    }),
  }),
});

export const {
  useGetBuilderSessionHistoryInfiniteQuery,
  useListBuilderSessionsQuery,
  useGetBuilderSessionQuery,
  useSendBuilderSessionMessageMutation,
  useUpdateBuilderSessionSkillsConfigMutation,
  useResetBuilderSessionMutation,
  useSendProductAgentSessionMessageMutation,
} = builderSessionApi;

// Reads a cached history entry without subscribing or fetching, so the chat
// can show pages loaded before a route change without requesting new ones.
export const useBuilderSessionHistoryCache =
  builderSessionApi.endpoints.getBuilderSessionHistory.useInfiniteQueryState;

export const streamProductAgentMessage = async (
  {
    appBuilderId,
    builderSessionId,
    content,
    clientMessageId,
    promptSelections,
    attachments,
    connectionAskResolution,
    model,
    signal,
  }: {
    appBuilderId: string;
    builderSessionId: string;
    content?: string;
    clientMessageId?: string;
    promptSelections?: Record<string, string>;
    attachments?: AppBuilderAttachment[];
    connectionAskResolution?: ConnectionAskResolution;
    model?: string;
    signal?: AbortSignal;
  },
  onMessage: (msg: BuilderMessage) => void,
): Promise<void> => {
  const isAbortError = (error: unknown): boolean =>
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError';

  const url = buildApiEndpointUrl(
    AppBuilderStreamAPIName,
    `/app-builders/${appBuilderId}/sessions/${builderSessionId}/product-agent/stream`,
  );
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({
      content: content ?? '',
      ...(clientMessageId ? { clientMessageId } : {}),
      promptSelections,
      ...(attachments && attachments.length > 0 ? { attachments } : {}),
      ...(connectionAskResolution ? { connectionAskResolution } : {}),
      ...(model ? { model } : {}),
    }),
    headers: { Accept: 'text/event-stream' },
    signal,
  });
  let response: Response;
  try {
    response = await fetch(url, requestInit);
  } catch (err) {
    // Caller aborted — not a real failure.
    if (isAbortError(err)) return;
    throw err;
  }
  if (!response.ok || !response.body) {
    // Preserve the response body and HTTP status so upstream can classify the
    // failure instead of collapsing everything to a generic stream error.
    let payload: AppBuilderErrorIdentity = {};
    try {
      payload = parseAppBuilderErrorPayload(await response.text());
    } catch {
      // The HTTP status still gives the classifier enough identity to choose
      // safe default copy when the response body cannot be read.
    }
    throw new AppBuilderRequestError({
      ...payload,
      message:
        payload.message || `PA stream failed with HTTP ${response.status}`,
      status: response.status,
      source: resolveAppBuilderErrorSource(payload, 'stream'),
    });
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let receivedDone = false;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() ?? '';
      for (const block of blocks) {
        if (!block.trim()) continue;
        let eventType = 'message';
        let data = '';
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) eventType = line.slice(6).trim();
          else if (line.startsWith('data:')) data = line.slice(5).trim();
        }
        if (eventType === 'message' && data) {
          try {
            const parsed = JSON.parse(data) as {
              event: string;
              item: BuilderMessage;
            };
            if (parsed.item?.id) onMessage(parsed.item);
          } catch {
            // skip malformed
          }
        }
        if (eventType === 'error') {
          // Mid-stream failures carry structured JSON in the SSE data field.
          const payload = parseAppBuilderErrorPayload(data);
          throw new AppBuilderRequestError({
            ...payload,
            source: resolveAppBuilderErrorSource(payload, 'stream'),
          });
        }
        if (eventType === 'done') {
          receivedDone = true;
          return;
        }
      }
    }
  } catch (err) {
    if (isAbortError(err)) return;
    throw err;
  } finally {
    reader.releaseLock();
  }
  if (!receivedDone && !signal?.aborted) {
    // Connection dropped before the server sent `done` — treat like a transient
    // stream failure so the composer can offer retry. A caller-initiated abort
    // (e.g. session/app switch) also ends the stream early, but the message was
    // already dispatched, so that is an expected close, not a failure.
    throw new AppBuilderRequestError({
      message: 'PA stream closed without a done event',
      source: 'stream',
    });
  }
};
